import {
    AppViews,
    EcologyHubViews,
    LoginViews,
    MapViews,
    ProjectViews,
    SurveyViews,
    TableViews,
    PortfolioViews,
    SiteNatureRiskViews,
} from 'src/routes/routesConfig';
import { SortOrder } from 'src/shared/types';
import { AdminViews, GNPViews, RiskPortfolioViews } from 'src/routes/routesConfig';
import { HabitatInsightViews } from 'src/routes/routesConfig';
import type { SiteExplorerTabs } from 'src/shared/types/siteExplorerTabs';

export type PortfolioSiteExplorerParams = {
    customerId: string;
    selectedMetric?: string; // allow species metric ids like 'possibleInvasive'
    sortDirection?: SortOrder;
    view?: SiteExplorerTabs;
};

export const navRoutesConfig = {
    home: '/',
    [AppViews.PROJECTS]: (params: { customerId: string }) => `/projects?customerId=${params.customerId}`,
    [AppViews.DASHBOARD]: (params: { projectId: string; customerId: string }) =>
        `/dashboard?projectId=${params.projectId}&customerId=${params.customerId}`,
    [AppViews.UPGRADE_SUBSCRIPTION]: (params: { projectId: string; customerId: string }) =>
        `/upgrade-subscription?projectId=${params.projectId}&customerId=${params.customerId}`,
    [AppViews.ECOLOGY_HUB]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub?projectId=${params.projectId}&customerId=${params.customerId}`,
    [MapViews.SPECIES_INSIGHTS_MAP]: (params: { projectId: string; customerId: string }) =>
        `/map/species-insights-map?projectId=${params.projectId}&customerId=${params.customerId}`,
    [MapViews.HABITAT_INSIGHTS_MAP]: (params: { projectId: string; customerId: string }) =>
        `/map/habitat-insights-map?projectId=${params.projectId}&customerId=${params.customerId}`,
    [SurveyViews.SURVEY_ADMIN]: (params: { customerId: string }) => `/survey/survey-admin?customerId=${params.customerId}`,
    [SurveyViews.SURVEY_DESIGN_SUMMARY]: (params: { customerId: string }) =>
        `/survey/survey-design-summary?customerId=${params.customerId}`,
    [SurveyViews.NEW_SURVEY_DESIGN]: (params: { customerId: string }) => `/survey/new-survey-design?customerId=${params.customerId}`,

    [TableViews.SAMPLE_METRICS]: (params: { projectId: string; customerId: string }) =>
        `/tables/sample-metrics?projectId=${params.projectId}&customerId=${params.customerId}`,
    [TableViews.HABITAT_INSIGHTS_METRICS]: (params: { projectId: string; customerId: string }) =>
        `/tables/habitat-insights-metrics?projectId=${params.projectId}&customerId=${params.customerId}`,

    [LoginViews.TERMS_OF_USE]: '/login/terms-of-use',
    [LoginViews.TERMS_OF_USE_DECLINED]: '/login/terms-of-use-declined',
    [LoginViews.IMPERSONATE_USER]: '/login/impersonate-user',
    [LoginViews.POST_AUTH_REDIRECT]: '/login/post-auth-redirect',

    [AdminViews.GNP]: '/admin/management',
    [GNPViews.ORG_MANAGEMENT]: (params: { customerId: string }) => `/admin/management/nature-performance?customerId=${params.customerId}`,
    [AdminViews.ORGANISATIONS]: '/admin/organisations',
    [AdminViews.USER_MANAGEMENT]: (params: { customerId: string }) => `/admin/user-management?customerId=${params.customerId}`,
    [AdminViews.RISK_PORTFOLIO]: (params: { customerId: string }) => `/admin/management/nature-risk?customerId=${params.customerId}`,
    [RiskPortfolioViews.SITE_MANIFEST_PREVIEW]: (params: { customerId: string }) =>
        `/admin/management/nature-risk/manifest-preview?customerId=${params.customerId}`,
    [AdminViews.ENGINE_API_TOKENS]: '/admin/engine-api-tokens',
    [AdminViews.ESTATE_MANAGEMENT]: '/admin/estate-management',
    [AdminViews.GEOSPATIAL_CATALOGUE]: '/admin/catalogue',

    [EcologyHubViews.SPECIES_TRENDS]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub/species-trends?projectId=${params.projectId}&customerId=${params.customerId}`,
    [EcologyHubViews.HABITAT_TRENDS]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub/habitat-trends?projectId=${params.projectId}&customerId=${params.customerId}`,
    [EcologyHubViews.SAMPLE_COMPARISON]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub/sample-comparison?projectId=${params.projectId}&customerId=${params.customerId}`,
    [EcologyHubViews.STATISTICAL_COMPARISON]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub/statistical-comparison?projectId=${params.projectId}&customerId=${params.customerId}`,
    [EcologyHubViews.TAXONOMIC_COMPOSITION]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub/taxonomic-composition?projectId=${params.projectId}&customerId=${params.customerId}`,
    [EcologyHubViews.COMMUNITY_COMPARISON]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub/community-comparison?projectId=${params.projectId}&customerId=${params.customerId}`,

    [ProjectViews.PROJECT_DEFINITION]: (params: { customerId: string; projectId: string }) =>
        `/projects/project-definition?customerId=${params.customerId}&projectId=${params.projectId}`,
    [ProjectViews.NEW_PROJECT_DEFINITION]: (params: { customerId: string; projectId?: string }) =>
        `/projects/new-project-definition?customerId=${params.customerId}${params.projectId ? `&projectId=${params.projectId}` : ''}`,
    [ProjectViews.EDIT_PROJECT_DEFINITION]: (params: { customerId: string; projectId: string }) =>
        `/projects/edit-project-definition?customerId=${params.customerId}&projectId=${params.projectId}`,
    [ProjectViews.FREEMIUM_SPECIES_INSIGHTS]: (params: { customerId: string; projectId: string }) =>
        `/projects/freemium-species-insights?customerId=${params.customerId}&projectId=${params.projectId}`,
    [ProjectViews.SITE_FILES]: (params: { customerId: string; projectId: string; filterFileType?: string }) =>
        `/projects/site-files?customerId=${params.customerId}&projectId=${params.projectId}${
            params.filterFileType ? `&filterFileType=${params.filterFileType}` : ''
        }`,
    [ProjectViews.SITE_SUMMARY]: (params: { projectId: string; customerId: string }) =>
        `/projects/site-summary?projectId=${params.projectId}&customerId=${params.customerId}`,

    [HabitatInsightViews.HABITAT_INSIGHTS_UPLOAD]: (params: { customerId: string; projectId?: string }) =>
        `/habitat-insights/habitat-insights-upload?customerId=${params.customerId}${params.projectId && `&projectId=${params.projectId}`}`,
    [HabitatInsightViews.HABITAT_INSIGHTS_ADD_SITE]: '/habitat-insights/add-site-to-platform',

    [PortfolioViews.PORTFOLIO_SUMMARY]: (params: { customerId: string }) => `/portfolio?customerId=${params.customerId}`,
    [PortfolioViews.PORTFOLIO_SITE_EXPLORER]: (params: PortfolioSiteExplorerParams) => {
        const baseUrl = `/portfolio/site-explorer?customerId=${params.customerId}`;
        const selectedMetric = params.selectedMetric ? `&selectedMetric=${params.selectedMetric}` : '';
        const sortDirection = params.sortDirection ? `&sortDirection=${params.sortDirection}` : '';
        const view = params.view ? `&view=${params.view}` : '';
        return `${baseUrl}${selectedMetric}${sortDirection}${view}`;
    },
    [PortfolioViews.SPECIES_RISK_INDICATORS]: (params: {
        customerId: string;
        tab?: 'iucn-red-list-species' | 'invasive-species';
        sortDirection?: SortOrder;
    }) =>
        `/portfolio/species-risk-indicators?customerId=${params.customerId}${params.tab ? `&tab=${params.tab || 'invasive-species'}` : ''}${
            params.sortDirection ? `&sortDirection=${params.sortDirection}` : ''
        }`,

    [PortfolioViews.GLOBAL_NATURE_RISK_OVERVIEW]: (params: { customerId: string }) =>
        `/portfolio/global-nature-risk/overview?customerId=${params.customerId}`,
    [PortfolioViews.GLOBAL_NATURE_RISK_STATE_OF_NATURE]: (params: { customerId: string }) =>
        `/portfolio/global-nature-risk/state-of-nature?customerId=${params.customerId}`,
    // `view` selects the tab and `focusSite` frames the map on one site. Both ride
    // in the URL, so a view survives a refresh and the user can send it to someone.
    [PortfolioViews.GLOBAL_NATURE_RISK_SITE_LIST]: (params: { customerId: string; view?: SiteExplorerTabs; focusSiteId?: string }) => {
        const query = new URLSearchParams({ customerId: params.customerId });
        if (params.view) query.set('view', params.view);
        if (params.focusSiteId) query.set('focusSite', params.focusSiteId);
        return `/portfolio/global-nature-risk/site-list?${query}`;
    },
    [PortfolioViews.GLOBAL_NATURE_RISK_IMPACTS_AND_DEPENDENCIES]: (params: { customerId: string }) =>
        `/portfolio/global-nature-risk/impacts-and-dependencies?customerId=${params.customerId}`,

    [SiteNatureRiskViews.OVERVIEW]: (params: { siteId: string; customerId: string }) =>
        `/site-nature-risk/${params.siteId}/overview?customerId=${params.customerId}`,
    [SiteNatureRiskViews.CHARACTERISTICS]: (params: { siteId: string; customerId: string }) =>
        `/site-nature-risk/${params.siteId}/characteristics?customerId=${params.customerId}`,
    [SiteNatureRiskViews.STATE_OF_NATURE]: (params: { siteId: string; customerId: string }) =>
        `/site-nature-risk/${params.siteId}/state-of-nature?customerId=${params.customerId}`,
    [SiteNatureRiskViews.IMPACTS_AND_DEPENDENCIES]: (params: { siteId: string; customerId: string }) =>
        `/site-nature-risk/${params.siteId}/impacts-and-dependencies?customerId=${params.customerId}`,
};
